// deploy sync 2026-04-08
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';
import AccessModal from './components/AccessModal';
import NavBar from './components/NavBar';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, RequireAuth, useAuth } from './components/AuthGate';
import { getAdminToken } from './services/platform-api';
import { ConvergedLayout } from './components/ConvergedLayout';
// BuilderShell removed — builder routes now use ConvergedPage

// ── V1 Dashboard quarantined — visitors redirect to /v2 ──────────────────────

// ── Lazy: catalog (public tool discovery) ────────────────────────────────────
const V2Catalog = lazy(() => import('./pages/v2/Catalog'));
const V2ToolDetail = lazy(() => import('./pages/v2/ToolDetailV2'));

// ── Lazy: operator surface /v2/ops/* (ADR-026 Wave 4A + ADR-027 Wave 4B) ─────
const OpsLayout = lazy(() => import('./components/ops/OpsLayout').then(m => ({ default: m.OpsLayout })));
const ChainWalk = lazy(() => import('./pages/ops/ChainWalk'));
const OperatorExecutionReview = lazy(() => import('./pages/ops/OperatorExecutionReview'));
const AgentProfile = lazy(() => import('./pages/ops/AgentProfile'));
const AgentList = lazy(() => import('./pages/ops/AgentList'));
const ChainList = lazy(() => import('./pages/ops/ChainList'));
const CoherenceReview = lazy(() => import('./pages/ops/CoherenceReview'));
const WarrantList = lazy(() => import('./pages/ops/WarrantInspection').then(m => ({ default: m.WarrantList })));
const WarrantDetail = lazy(() => import('./pages/ops/WarrantInspection').then(m => ({ default: m.WarrantDetail })));
const BundleDetail = lazy(() => import('./pages/ops/BundleDetail'));
const AuditTrail = lazy(() => import('./pages/ops/AuditTrail'));
const ShadowReview = lazy(() => import('./pages/ops/ShadowReview'));
// NEXUM-C1-OPERATOR-SHELL-CONVERGENCE-1 — B-series review surfaces.
const OperatorReview = lazy(() => import('./pages/OperatorReview'));

// ── Lazy: developer portal (kept for API key CRUD) ──────────────────────────
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal'));

// ── Lazy: builder workspace (demoted, functional) ────────────────────────────
const BuilderCapabilities = lazy(() => import('./pages/BuilderCapabilities'));
const WorkflowList = lazy(() => import('./pages/builder/WorkflowList'));
const WorkflowDetail = lazy(() => import('./pages/builder/WorkflowDetail'));
const WorkflowEditor = lazy(() => import('./pages/builder/WorkflowEditor'));
const OrchestrationEditor = lazy(() => import('./pages/builder/OrchestrationEditor'));
const RunList = lazy(() => import('./pages/builder/RunList'));
const RunDetail = lazy(() => import('./pages/builder/RunDetail'));
const BuilderLibrary = lazy(() => import('./pages/builder/Library'));

// ── Lazy: converged control plane — Mission Control ──────────────────────────
const MissionControl = lazy(() => import('./pages/control/mission/MissionControl'));
const AdmissionsInbox = lazy(() => import('./pages/control/AdmissionsInbox'));
const PromotionQueueView = lazy(() => import('./pages/control/PromotionQueueView'));
const FirstRunGuide = lazy(() => import('./pages/control/FirstRunGuide'));
const OnboardingSetup = lazy(() => import('./pages/control/OnboardingSetup'));

// ── Lazy: converged control plane — Agents ───────────────────────────────────
const ExternalAgentInventory = lazy(() => import('./pages/control/ExternalAgentInventory'));
const ExternalAgentDetail = lazy(() => import('./pages/control/ExternalAgentDetail'));
const AgentPortrait = lazy(() => import('./pages/control/AgentPortrait'));
const PlatformObjectList = lazy(() => import('./pages/control/PlatformObjectList'));
const PlatformObjectDetail = lazy(() => import('./pages/control/PlatformObjectDetail'));
const LifecycleCardView = lazy(() => import('./pages/control/LifecycleCardView'));
const AgentMessaging = lazy(() => import('./pages/control/AgentMessaging'));

// ── Lazy: converged control plane — Coordination ─────────────────────────────
const DelegationAuditView = lazy(() => import('./pages/control/DelegationAuditView'));
const CoordinationSessionView = lazy(() => import('./pages/control/CoordinationSessionView'));

// ── Lazy: converged control plane — Evidence ─────────────────────────────────
const ExternalAgentDecisions = lazy(() => import('./pages/control/ExternalAgentDecisions'));
const ExecutionList = lazy(() => import('./pages/control/ExecutionList'));
const ExecutionDetail = lazy(() => import('./pages/control/ExecutionDetail'));
const ReplayInspector = lazy(() => import('./pages/control/ReplayInspector'));
const PrecursorView = lazy(() => import('./pages/control/PrecursorView'));
const RightView = lazy(() => import('./pages/control/RightView'));
const CorrelationTraceView = lazy(() => import('./pages/control/CorrelationTraceView'));
const DecisionDistributionView = lazy(() => import('./pages/control/DecisionDistributionView'));
const BlockReasonAnalysisView = lazy(() => import('./pages/control/BlockReasonAnalysisView'));
const TrustHistoryView = lazy(() => import('./pages/control/TrustHistoryView'));
const IntelligenceView = lazy(() => import('./pages/control/IntelligenceView'));
const GovernanceAnalytics = lazy(() => import('./pages/control/GovernanceAnalytics'));

// ── Lazy: converged control plane — Governance ───────────────────────────────
const OversightCenter = lazy(() => import('./pages/control/OversightCenter'));
const InterventionControlsView = lazy(() => import('./pages/control/InterventionControlsView'));
const VerificationCenter = lazy(() => import('./pages/control/VerificationCenter'));
const CredentialCenter = lazy(() => import('./pages/control/CredentialCenter'));
const TenantBoundaryAdmin = lazy(() => import('./pages/control/TenantBoundaryAdmin'));
const RecommendationQueueView = lazy(() => import('./pages/control/RecommendationQueueView'));
const RecommendationDetailView = lazy(() => import('./pages/control/RecommendationDetailView'));
const GovernanceCenter = lazy(() => import('./pages/control/GovernanceCenter'));
const SelfRegQueue = lazy(() => import('./pages/control/SelfRegQueue'));
const SelfRegDetail = lazy(() => import('./pages/control/SelfRegDetail'));
const OnboardingWizard = lazy(() => import('./pages/control/OnboardingWizard'));
const QuickConnect = lazy(() => import('./pages/control/QuickConnect'));
const BootstrapTokenAdmin = lazy(() => import('./pages/control/BootstrapTokenAdmin'));
const GovernanceOverview = lazy(() => import('./pages/control/GovernanceOverview'));
const PolicyEditor = lazy(() => import('./pages/control/PolicyEditor'));
const PolicyOperations = lazy(() => import('./pages/control/PolicyOperations'));
const PackHistory = lazy(() => import('./pages/control/PackHistory'));
const GovernanceProgramEditor = lazy(() => import('./pages/control/GovernanceProgramEditor'));
const DomainPackCatalog = lazy(() => import('./pages/control/DomainPackCatalog'));
const PersonaOverlayCatalog = lazy(() => import('./pages/control/PersonaOverlayCatalog'));
const TenantPolicyProfileCatalog = lazy(() => import('./pages/control/TenantPolicyProfileCatalog'));
const OutcomeReview = lazy(() => import('./pages/control/OutcomeReview'));
const ObserveArtifacts = lazy(() => import('./pages/control/ObserveArtifacts'));
const ReleasePackets = lazy(() => import('./pages/control/ReleasePackets'));
const TrustOperations = lazy(() => import('./pages/control/TrustOperations'));
const CoordinationHub = lazy(() => import('./pages/control/CoordinationHub'));
const DelegationOverview = lazy(() => import('./pages/control/DelegationOverview'));
const EvidenceHub = lazy(() => import('./pages/control/EvidenceHub'));
const WorkThreadView = lazy(() => import('./pages/control/WorkThreadView'));
const RegimeWorkshop = lazy(() => import('./pages/control/RegimeWorkshop'));

// ── Loading fallback ─────────────────────────────────────────────────────────
function PageLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, color: 'var(--v2-text-muted, #888)', fontFamily: 'var(--v2-font, sans-serif)' }}>
      Loading...
    </div>
  );
}

/** Wraps converged control-plane pages: auth + ConvergedLayout. */
function ConvergedPage({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <ConvergedLayout>{children}</ConvergedLayout>
    </RequireAuth>
  );
}

/** Smart home: authenticated → check onboarding → Mission Control or Setup, visitor → login. */
function SmartHome() {
  const { accessLevel, hydrating } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    if (accessLevel === 'visitor' || hydrating) return;
    const BASE = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';
    const token = getAdminToken();
    if (!token) { setOnboardingChecked(true); return; }
    fetch(`${BASE}/api/v1/onboarding/status`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && !data.onboarding_complete && data.agent_count === 0) setNeedsSetup(true);
        setOnboardingChecked(true);
      })
      .catch(() => setOnboardingChecked(true));
  }, [accessLevel, hydrating]);

  if (hydrating) return <PageLoading />;
  if (accessLevel !== 'visitor') {
    if (!onboardingChecked) return <PageLoading />;
    if (needsSetup) return <Navigate to="/control/setup" replace />;
    return <Navigate to="/control/mission" replace />;
  }

  // On the operator dashboard domain, show login directly
  const isDashDomain = typeof window !== 'undefined' &&
    (window.location.hostname === 'dash.kethral.com' || window.location.hostname === 'localhost');

  if (isDashDomain) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#0f172a', color: '#e2e8f0',
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
        padding: 24,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #6366f1, #a855f7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: '#fff', fontSize: 24,
          }}>N</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px', color: '#fff' }}>Nexum Protocol</h1>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 8px', lineHeight: 1.6 }}>
            Governed execution for AI agents.
          </p>
          <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 28px', lineHeight: 1.5 }}>
            Control what agents can do. Require human approval when needed. See why every decision was made.
          </p>
          <button
            onClick={() => setShowLogin(true)}
            style={{
              background: '#6366f1', color: '#fff', border: 'none',
              padding: '12px 32px', borderRadius: 8, fontSize: 15,
              fontWeight: 600, cursor: 'pointer', width: '100%',
              minHeight: 44,
            }}
          >
            Operator Login
          </button>
          <p style={{ fontSize: 11, color: '#475569', marginTop: 24 }}>
            Early access &middot; Kethral Inc.
          </p>
        </div>
        <AccessModal open={showLogin} onClose={() => setShowLogin(false)} />
      </div>
    );
  }

  // Non-dash visitors: redirect to control plane (will hit RequireAuth → login)
  return <Navigate to="/control/mission" replace />;
}

/** Conditionally show NavBar only on non-converged routes. */
function ConditionalNavBar() {
  const location = useLocation();
  // Hide NavBar on home route (SmartHome handles its own UI)
  if (location.pathname === '/') return null;
  // Hide NavBar on all converged routes (sidebar replaces it)
  if (location.pathname.startsWith('/control/')) return null;
  if (location.pathname.startsWith('/developer')) return null;
  if (location.pathname.startsWith('/v2/catalog')) return null;
  if (location.pathname === '/developers') return null;
  return <NavBar />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="nx-app">
          <ConditionalNavBar />
          <ErrorBoundary>
            <main className="nx-main">
              <Suspense fallback={<PageLoading />}>
                <Routes>
                  {/* ── Smart Home ──────────────────────────────────────── */}
                  <Route path="/" element={<SmartHome />} />

                  {/* ── Catalog (public tool discovery) ─────────────────── */}
                  <Route path="/v2/catalog" element={<ConvergedPage><V2Catalog /></ConvergedPage>} />
                  <Route path="/v2/catalog/:toolId" element={<ConvergedPage><V2ToolDetail /></ConvergedPage>} />

                  {/* ── Redirects: v2 marketing → control plane ─────────── */}
                  <Route path="/v2" element={<Navigate to="/control/mission" replace />} />
                  <Route path="/v2/developer" element={<Navigate to="/developer" replace />} />

                  {/* ── Operator surface /v2/ops/* (ADR-026 Wave 4A + ADR-027 Wave 4B) ──── */}
                  <Route path="/v2/ops" element={<OpsLayout />}>
                    <Route index element={<Navigate to="/v2/ops/chains" replace />} />
                    {/* OPERATOR-CONTROL-DISCOVERABILITY-1: /v2/ops/chains was
                        a self-redirect loop; now serves the ChainList landing. */}
                    <Route path="chains" element={<ChainList />} />
                    <Route path="chains/:correlation_root_id" element={<ChainWalk />} />
                    <Route path="review/:correlation_root_id" element={<OperatorExecutionReview />} />
                    <Route path="findings" element={<CoherenceReview />} />
                    <Route path="warrants" element={<WarrantList />} />
                    <Route path="warrants/:warrant_id" element={<WarrantDetail />} />
                    <Route path="bundles/:bundle_id" element={<BundleDetail />} />
                    <Route path="audit" element={<AuditTrail />} />
                    <Route path="enforcement-shadow" element={<ShadowReview />} />
                    {/* NEXUM-C1-OPERATOR-SHELL-CONVERGENCE-1 — B-series review surfaces. */}
                    <Route path="admin-review" element={<OperatorReview />} />
                  </Route>

                  {/* ── Agent surface /v2/agents/* (AGENT-PROFILE-1 + DISCOVERABILITY-1) ── */}
                  {/* Wrapped in OpsLayout so it lives in the same operator
                      shell as Operator Review and ChainWalk. Distinct from
                      the legacy developer-grade /control/external-agents/*
                      surfaces. */}
                  <Route path="/v2" element={<OpsLayout />}>
                    <Route path="agents" element={<AgentList />} />
                    <Route path="agents/:agent_id" element={<AgentProfile />} />
                  </Route>

                  {/* ── API Keys (developer key CRUD) ──────────────────── */}
                  <Route path="/developers" element={<ConvergedPage><DeveloperPortal /></ConvergedPage>} />

                  {/* ── Builder Workspace (integrated into control shell) ── */}
                  <Route path="/developer" element={<ConvergedPage><BuilderCapabilities /></ConvergedPage>} />
                  <Route path="/developer/capabilities" element={<ConvergedPage><BuilderCapabilities /></ConvergedPage>} />
                  <Route path="/developer/capabilities/:objectId" element={<ConvergedPage><BuilderCapabilities /></ConvergedPage>} />
                  <Route path="/developer/workflows" element={<ConvergedPage><WorkflowList /></ConvergedPage>} />
                  <Route path="/developer/workflows/new" element={<ConvergedPage><WorkflowEditor /></ConvergedPage>} />
                  <Route path="/developer/workflows/:id" element={<ConvergedPage><WorkflowDetail /></ConvergedPage>} />
                  <Route path="/developer/workflows/:id/edit" element={<ConvergedPage><WorkflowEditor /></ConvergedPage>} />
                  <Route path="/developer/orchestration/:id/edit" element={<ConvergedPage><OrchestrationEditor /></ConvergedPage>} />
                  <Route path="/developer/runs" element={<ConvergedPage><RunList /></ConvergedPage>} />
                  <Route path="/developer/runs/:id" element={<ConvergedPage><RunDetail /></ConvergedPage>} />
                  <Route path="/developer/library" element={<ConvergedPage><BuilderLibrary /></ConvergedPage>} />

                  {/* ── Mission Control (canonical operator home) ──────── */}
                  <Route path="/control/mission" element={<ConvergedPage><MissionControl /></ConvergedPage>} />
                  <Route path="/control/intake" element={<ConvergedPage><AdmissionsInbox /></ConvergedPage>} />
                  <Route path="/control/readiness" element={<ConvergedPage><PromotionQueueView /></ConvergedPage>} />
                  <Route path="/control/getting-started" element={<ConvergedPage><FirstRunGuide /></ConvergedPage>} />
                  <Route path="/control/setup" element={<RequireAuth><OnboardingSetup /></RequireAuth>} />

                  {/* ── Agents (primary fleet) ─────────────────────────── */}
                  <Route path="/control/external-agents" element={<ConvergedPage><ExternalAgentInventory /></ConvergedPage>} />
                  <Route path="/control/external-agents/:agentId" element={<ConvergedPage><ExternalAgentDetail /></ConvergedPage>} />
                  <Route path="/control/external-agents/:agentId/portrait" element={<ConvergedPage><AgentPortrait /></ConvergedPage>} />
                  <Route path="/control/objects" element={<ConvergedPage><PlatformObjectList /></ConvergedPage>} />
                  <Route path="/control/objects/:objectId" element={<ConvergedPage><PlatformObjectDetail /></ConvergedPage>} />
                  <Route path="/control/overview/:objectId" element={<ConvergedPage><LifecycleCardView /></ConvergedPage>} />
                  <Route path="/control/agent-messages" element={<ConvergedPage><AgentMessaging /></ConvergedPage>} />
                  <Route path="/control/regime/:agentId" element={<ConvergedPage><RegimeWorkshop /></ConvergedPage>} />

                  {/* ── Delegation & Coordination ─────────────────────── */}
                  <Route path="/control/delegation" element={<ConvergedPage><DelegationOverview /></ConvergedPage>} />
                  <Route path="/control/coordination" element={<ConvergedPage><CoordinationHub /></ConvergedPage>} />
                  <Route path="/control/delegations" element={<ConvergedPage><DelegationAuditView /></ConvergedPage>} />
                  <Route path="/control/sessions" element={<ConvergedPage><CoordinationSessionView /></ConvergedPage>} />

                  {/* ── Evidence ────────────────────────────────────────── */}
                  <Route path="/control/evidence" element={<ConvergedPage><EvidenceHub /></ConvergedPage>} />
                  <Route path="/control/agent-decisions" element={<ConvergedPage><ExternalAgentDecisions /></ConvergedPage>} />
                  <Route path="/control/executions" element={<ConvergedPage><ExecutionList /></ConvergedPage>} />
                  <Route path="/control/executions/:executionId" element={<ConvergedPage><ExecutionDetail /></ConvergedPage>} />
                  <Route path="/control/executions/:executionId/replay" element={<ConvergedPage><ReplayInspector /></ConvergedPage>} />
                  <Route path="/control/executions/:executionId/precursor" element={<ConvergedPage><PrecursorView /></ConvergedPage>} />
                  <Route path="/control/executions/:executionId/right" element={<ConvergedPage><RightView /></ConvergedPage>} />
                  <Route path="/control/correlation" element={<ConvergedPage><CorrelationTraceView /></ConvergedPage>} />
                  <Route path="/control/correlation/:correlationId" element={<ConvergedPage><CorrelationTraceView /></ConvergedPage>} />
                  <Route path="/control/analytics/decisions" element={<ConvergedPage><DecisionDistributionView /></ConvergedPage>} />
                  <Route path="/control/analytics/block-reasons" element={<ConvergedPage><BlockReasonAnalysisView /></ConvergedPage>} />
                  <Route path="/control/analytics/trust" element={<ConvergedPage><TrustHistoryView /></ConvergedPage>} />
                  <Route path="/control/intelligence" element={<ConvergedPage><IntelligenceView /></ConvergedPage>} />
                  <Route path="/control/governance-analytics" element={<ConvergedPage><GovernanceAnalytics /></ConvergedPage>} />
                  <Route path="/control/work/:correlationId" element={<ConvergedPage><WorkThreadView /></ConvergedPage>} />

                  {/* ── Governance ──────────────────────────────────────── */}
                  <Route path="/control/governance" element={<ConvergedPage><GovernanceOverview /></ConvergedPage>} />
                  <Route path="/control/policy-editor" element={<ConvergedPage><PolicyEditor /></ConvergedPage>} />
                  <Route path="/control/policy-operations" element={<ConvergedPage><PolicyOperations /></ConvergedPage>} />
                  <Route path="/control/pack-history" element={<ConvergedPage><PackHistory /></ConvergedPage>} />
                  <Route path="/control/governance-program" element={<ConvergedPage><GovernanceProgramEditor /></ConvergedPage>} />
                  <Route path="/control/domain-packs" element={<ConvergedPage><DomainPackCatalog /></ConvergedPage>} />
                  <Route path="/control/domain-packs/:slug" element={<ConvergedPage><DomainPackCatalog /></ConvergedPage>} />
                  <Route path="/control/persona-overlays" element={<ConvergedPage><PersonaOverlayCatalog /></ConvergedPage>} />
                  <Route path="/control/persona-overlays/:slug" element={<ConvergedPage><PersonaOverlayCatalog /></ConvergedPage>} />
                  <Route path="/control/tenant-profiles" element={<ConvergedPage><TenantPolicyProfileCatalog /></ConvergedPage>} />
                  <Route path="/control/governance/:objectId" element={<ConvergedPage><GovernanceCenter /></ConvergedPage>} />
                  <Route path="/control/outcome-review" element={<ConvergedPage><OutcomeReview /></ConvergedPage>} />
                  <Route path="/control/outcome-review/:assessmentId" element={<ConvergedPage><OutcomeReview /></ConvergedPage>} />
                  <Route path="/control/observe-artifacts" element={<ConvergedPage><ObserveArtifacts /></ConvergedPage>} />
                  <Route path="/control/observe-artifacts/:artifactId" element={<ConvergedPage><ObserveArtifacts /></ConvergedPage>} />
                  <Route path="/control/release-packets" element={<ConvergedPage><ReleasePackets /></ConvergedPage>} />
                  <Route path="/control/release-packets/:packetId" element={<ConvergedPage><ReleasePackets /></ConvergedPage>} />
                  <Route path="/control/trust-ops" element={<ConvergedPage><TrustOperations /></ConvergedPage>} />
                  <Route path="/control/credentials" element={<ConvergedPage><CredentialCenter /></ConvergedPage>} />
                  <Route path="/control/oversight-center" element={<ConvergedPage><OversightCenter /></ConvergedPage>} />
                  <Route path="/control/intervention" element={<ConvergedPage><InterventionControlsView /></ConvergedPage>} />
                  <Route path="/control/verification" element={<ConvergedPage><VerificationCenter /></ConvergedPage>} />
                  <Route path="/control/boundaries" element={<ConvergedPage><TenantBoundaryAdmin /></ConvergedPage>} />
                  <Route path="/control/recommendations" element={<ConvergedPage><RecommendationQueueView /></ConvergedPage>} />
                  <Route path="/control/recommendations/:recommendationId" element={<ConvergedPage><RecommendationDetailView /></ConvergedPage>} />
                  <Route path="/control/selfreg-queue" element={<ConvergedPage><SelfRegQueue /></ConvergedPage>} />
                  <Route path="/control/selfreg-queue/:requestId" element={<ConvergedPage><SelfRegDetail /></ConvergedPage>} />
                  <Route path="/control/onboarding" element={<ConvergedPage><OnboardingWizard /></ConvergedPage>} />
                  <Route path="/control/onboarding/:draftId" element={<ConvergedPage><OnboardingWizard /></ConvergedPage>} />
                  <Route path="/control/quick-connect" element={<ConvergedPage><QuickConnect /></ConvergedPage>} />
                  <Route path="/control/bootstrap-tokens" element={<ConvergedPage><BootstrapTokenAdmin /></ConvergedPage>} />

                  {/* ── Redirects for killed routes ────────────────────── */}
                  <Route path="/search" element={<Navigate to="/v2/catalog" replace />} />
                  <Route path="/tools/:toolId" element={<Navigate to="/v2/catalog" replace />} />
                  <Route path="/register" element={<Navigate to="/developer" replace />} />
                </Routes>
              </Suspense>
            </main>
          </ErrorBoundary>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
